import React, { useState, useRef, useEffect } from "react";
import {
  Box,
  Divider,
  Spacer,
  Stack,
  Typography,
  Button,
  Toast,
} from "digiwash-components";

import Modal from "react-native-modal";
import Variation from "../../components/Variation";
import { formatPrice } from "digiwash-core";
import TabBar from "../../components/TabBar";
import CustomIcon from "../../components/CustomIcon";
import useCart from "../../ducks/cart/hooks/useCart";
import { CartRetrieveOutput } from "../../ducks/cart/cart-types";
import { useUpdateCartItem } from "../../hooks/useUpdateCartItem";
import { quantityByServiceAndVariation } from "../../ducks/cart/cart-helpers";
import { useSelector } from "react-redux";
import userSelectors from "../../ducks/user/selectors";
import { ListServicesType } from "../../types/server";
import { useDebouncedCallback } from "use-debounce";
import { logEvent } from "../../helpers/analytics-helper";
import { EVENT_TYPES } from "../../types/analytics-type";
import {
  service_as_ga_change_to_cart,
  service_to_ga_item,
} from "../../helpers/ga-helper";

interface PropsType {
  selectedService: ListServicesType;
  visible: boolean;
  dismiss: () => void;
  onLogin: () => void;
}

function ModalService(props: PropsType) {
  let [selectedChildrenIndex, setSelectedChildrenIndex] = useState(0);
  let { retrieve: retrieveCart, revalidateCart } = useCart();
  let cart: CartRetrieveOutput = retrieveCart({ force: true });
  let cartData = cart?.data;
  let { addItem, removeItem } = useUpdateCartItem();
  let childrenOfSelectedService =
    props.selectedService?.children[selectedChildrenIndex];
  let authenticated: boolean = useSelector(userSelectors.getToken);
  let ref = useRef();

  let currentService = !!childrenOfSelectedService
    ? childrenOfSelectedService
    : props.selectedService;

  const debouncedHandlerForServiceClick = useDebouncedCallback(
    (value: number, type: "plus" | "minus") => {
      if (value === 0 && ref.current) {
        ref.current.show("آیتم از سبد خرید شما حذف شد.", {
          data: { type: "success" },
        });
      } else if (value > 0 && type === "plus" && ref.current) {
        ref.current.show("آیتم به سبد خرید شما افزوده شد.", {
          data: { type: "success" },
        });
      }
    },
    555
  );

  let currentServiceTitle = currentService?.name;
  useEffect(() => {
    if (currentServiceTitle) {
      logEvent(EVENT_TYPES.VIEW_ITEM, service_to_ga_item(currentService));
    }
  }, [currentServiceTitle]);

  return (
    <Modal
      isVisible={props.visible}
      onSwipeComplete={props.dismiss}
      onBackdropPress={props.dismiss}
      swipeDirection="down"
      style={{
        justifyContent: "flex-end",
        margin: "auto",
        width: "100%",
        alignItems: "center",
      }}
    >
      <Box
        style={{
          borderTopRightRadius: 16,
          borderTopLeftRadius: 16,
          width: "100%",
          maxWidth: 768,
        }}
        bg="common.white"
        px="l"
        py="xxl"
      >
        <Typography ff="mainBold" color="common.black">
          {props.selectedService?.name}
        </Typography>
        {!authenticated ? (
          <>
            <Divider size="l" />
            <Box bg="red.light500" br="l" px="l" py="m">
              <Stack distribution="space-between">
                <Stack>
                  <CustomIcon icon="warning" width={16} height={16} />
                  <Spacer size="m" />
                  <Typography fs="xm" color="red.dark">
                    برای ثبت سفارش ابتدا وارد شوید
                  </Typography>
                </Stack>
                <Box>
                  <Button
                    prefix={<Spacer size="m" />}
                    suffix={<Spacer size="m" />}
                    bg="primary.main"
                    size="s"
                    onPress={() => {
                      props.onLogin();
                    }}
                  >
                    ثبت نام/ ورود
                  </Button>
                </Box>
              </Stack>
            </Box>
          </>
        ) : null}

        {props.selectedService?.children.length > 0 ? (
          <>
            <Divider size="xxl" />
            <TabBar
              data={props.selectedService?.children}
              onSelect={(index) => {
                setSelectedChildrenIndex(index);
              }}
              selected={selectedChildrenIndex}
            />
          </>
        ) : null}

        {currentService?.description ? (
          <>
            <Divider size="l" />
            <Box bg="primary.light920" p="m" br="xm">
              <Stack>
                <CustomIcon icon="infoRounded" width={24} height={24} />
                <Spacer size="s" />
                <Typography color="common.black">
                  {currentService.description}
                </Typography>
              </Stack>
            </Box>
          </>
        ) : null}

        <Divider size="l" />
        {currentService?.pricing.map((item) => {
          return (
            <React.Fragment key={`${currentService.id}_${item.id}`}>
              <Box bg="primary.light920" p="l" br="xm">
                <Stack distribution="space-between">
                  <Stack>
                    <CustomIcon
                      icon={item.variation.alias}
                      width={48}
                      height={48}
                    />
                    <Spacer size="l" />
                    <Box>
                      <Typography color="common.black">
                        {item.variation.name}
                      </Typography>
                      <Typography ff="mainBold" color="common.black">
                        {item.price > 0
                          ? `${formatPrice(item.price)} تومان`
                          : "نیاز به کارشناسی"}
                      </Typography>
                    </Box>
                  </Stack>
                  {authenticated ? (
                    <Variation
                      key={`${item.id}-${item.variation.id}`}
                      quantity={quantityByServiceAndVariation({
                        cart: cartData,
                        service: currentService.id,
                        variation: item.variation.id,
                      })}
                      onSelectPlus={(count) => {
                        addItem({
                          service: currentService.id,
                          variation: item.variation.id,
                        }).then(() => {
                          revalidateCart();
                          logEvent(
                            EVENT_TYPES.ADD_TO_CART,
                            service_as_ga_change_to_cart(
                              item.price,
                              currentService,
                              item.variation
                            )
                          );
                          debouncedHandlerForServiceClick(count, "plus");
                        });
                      }}
                      onSelectMinus={(count) => {
                        removeItem({
                          service: currentService.id,
                          variation: item.variation.id,
                        }).then(() => {
                          logEvent(
                            EVENT_TYPES.REMOVE_FROM_CART,
                            service_as_ga_change_to_cart(
                              item.price,
                              currentService,
                              item.variation
                            )
                          );
                          revalidateCart();
                          debouncedHandlerForServiceClick(count, "minus");
                        });
                      }}
                    />
                  ) : null}
                </Stack>
              </Box>
              <Divider size="l" />
            </React.Fragment>
          );
        })}
      </Box>
      <Toast ref={ref} />
    </Modal>
  );
}

export default ModalService;
